// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: mail signatures (Settings → Mail → Signatures, and the composer's signature
// switcher). One document per user on the server; saves are optimistic and roll back
// to the last confirmed document when the PUT fails.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import type {KonvoMailComposeMode} from '@app/features/konvo_mail/state/KonvoMail';
import {
	buildSignatureCandidates,
	type MailSignatureCandidate,
	type MailSignatureSource,
	signatureText,
} from '@app/features/konvo_mail/utils/MailSignatureImport';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('MailSignatures');

export const MAX_MAIL_SIGNATURES = 10;

export type MailSignatureSourceKind = 'outlook' | 'konvo';

export interface MailSignature {
	id: string;
	name: string;
	html: string;
	source: MailSignatureSourceKind;
	updated_at: string;
}

export interface MailSignatureDoc {
	signatures: Array<MailSignature>;
	default_new: string | null;
	default_reply: string | null;
}

export type MailSignatureSaveError = 'too_large' | 'failed';

const EMPTY_DOC: MailSignatureDoc = {signatures: [], default_new: null, default_reply: null};

const normalisedText = (html: string): string => signatureText(html).replace(/\s+/g, '').toLowerCase();

/** A server-safe id (`^[a-z0-9-]{1,40}$`) from a name, unique among `taken`. */
export function signatureIdFor(name: string, taken: Iterable<string>): string {
	const used = new Set(taken);
	const base =
		name
			.normalize('NFKD')
			.replace(/[\u0300-\u036f]/g, '')
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, '-')
			.replace(/^-+|-+$/g, '')
			.slice(0, 32)
			.replace(/-+$/, '') || 'signature';
	if (!used.has(base)) return base;
	for (let n = 2; ; n++) {
		const id = `${base}-${n}`;
		if (!used.has(id)) return id;
	}
}

function coerceDoc(body: Partial<MailSignatureDoc> | null | undefined): MailSignatureDoc {
	const signatures = Array.isArray(body?.signatures)
		? body.signatures.filter((s): s is MailSignature => Boolean(s && typeof s.id === 'string'))
		: [];
	const ids = new Set(signatures.map((s) => s.id));
	const pick = (id: string | null | undefined) => (id && ids.has(id) ? id : null);
	return {
		signatures: signatures.map((s) => ({
			id: s.id,
			name: s.name ?? '',
			html: s.html ?? '',
			source: s.source === 'outlook' ? 'outlook' : 'konvo',
			updated_at: s.updated_at ?? '',
		})),
		default_new: pick(body?.default_new),
		default_reply: pick(body?.default_reply),
	};
}

class MailSignaturesStore {
	signatures: Array<MailSignature> = [];
	defaultNew: string | null = null;
	defaultReply: string | null = null;
	loaded = false;
	loading = false;
	loadError = false;
	saving = false;
	saveError: MailSignatureSaveError | null = null;

	private loadPromise: Promise<void> | null = null;
	private confirmed: MailSignatureDoc = EMPTY_DOC;
	private saveSeq = 0;

	constructor() {
		makeAutoObservable<this, 'loadPromise' | 'confirmed' | 'saveSeq'>(
			this,
			{loadPromise: false, confirmed: false, saveSeq: false},
			{autoBind: true},
		);
	}

	get doc(): MailSignatureDoc {
		return {signatures: this.signatures, default_new: this.defaultNew, default_reply: this.defaultReply};
	}

	/** Fetches once; later calls reuse the first result. A failed load can be retried. */
	load(): Promise<void> {
		if (!this.loadPromise) this.loadPromise = this.fetch();
		return this.loadPromise;
	}

	reload(): Promise<void> {
		this.loadPromise = this.fetch();
		return this.loadPromise;
	}

	byId(id: string | null | undefined): MailSignature | null {
		if (!id) return null;
		return this.signatures.find((signature) => signature.id === id) ?? null;
	}

	defaultFor(mode: KonvoMailComposeMode): MailSignature | null {
		return this.byId(mode === 'new' ? this.defaultNew : this.defaultReply);
	}

	/** Replaces the whole document (optimistic). Resolves false when the server refused it. */
	async save(doc: MailSignatureDoc): Promise<boolean> {
		const seq = ++this.saveSeq;
		const next = coerceDoc(doc);
		this.apply(next);
		this.saving = true;
		this.saveError = null;
		try {
			const response = await http.put<MailSignatureDoc>(Endpoints.KONVO_MAIL_SIGNATURES, {body: next});
			const stored = coerceDoc(response.body ?? next);
			runInAction(() => {
				this.confirmed = stored;
				if (seq === this.saveSeq) this.apply(stored);
			});
			return true;
		} catch (error) {
			logger.warn('Failed to save mail signatures', error);
			const tooLarge = (error as {status?: number} | null)?.status === 413;
			runInAction(() => {
				if (seq === this.saveSeq) {
					this.apply(this.confirmed);
					this.saveError = tooLarge ? 'too_large' : 'failed';
				}
			});
			return false;
		} finally {
			runInAction(() => {
				if (seq === this.saveSeq) this.saving = false;
			});
		}
	}

	/** Adds or replaces one signature (keeps its position when it exists). */
	upsert(signature: Omit<MailSignature, 'updated_at'>): Promise<boolean> {
		const stamped: MailSignature = {...signature, updated_at: new Date().toISOString()};
		const exists = this.signatures.some((s) => s.id === signature.id);
		const signatures = exists
			? this.signatures.map((s) => (s.id === signature.id ? stamped : s))
			: [...this.signatures, stamped];
		return this.save({...this.doc, signatures});
	}

	remove(id: string): Promise<boolean> {
		return this.save({
			signatures: this.signatures.filter((s) => s.id !== id),
			default_new: this.defaultNew === id ? null : this.defaultNew,
			default_reply: this.defaultReply === id ? null : this.defaultReply,
		});
	}

	setDefault(kind: 'new' | 'reply', id: string | null): Promise<boolean> {
		return this.save({
			...this.doc,
			default_new: kind === 'new' ? id : this.defaultNew,
			default_reply: kind === 'reply' ? id : this.defaultReply,
		});
	}

	/** A fresh id for a new signature called `name`. */
	newId(name: string): string {
		return signatureIdFor(
			name,
			this.signatures.map((s) => s.id),
		);
	}

	/** True when a signature with the same text is already saved. */
	hasSignatureLike(html: string): boolean {
		const text = normalisedText(html);
		return this.signatures.some((s) => normalisedText(s.html) === text);
	}

	/** Scans recent sent mail for Outlook signatures. Rejects when the server call fails. */
	/** `selfAddress` lets a reply sent without a signature still yield the sender's own, quoted under it. */
	async discover(selfAddress?: string | null): Promise<Array<MailSignatureCandidate>> {
		const response = await http.get<{sources?: Array<MailSignatureSource>}>(Endpoints.KONVO_MAIL_SIGNATURE_SOURCES);
		return buildSignatureCandidates(response.body?.sources ?? [], selfAddress);
	}

	/**
	 * Adds candidates as Outlook signatures (skipping ones already saved), and sets the
	 * suggested defaults where none is set yet. Resolves false when the save failed.
	 */
	importCandidates(candidates: ReadonlyArray<MailSignatureCandidate>): Promise<boolean> {
		const signatures = [...this.signatures];
		const taken = new Set(signatures.map((s) => s.id));
		const known = new Set(signatures.map((s) => normalisedText(s.html)));
		const now = new Date().toISOString();
		let defaultNew = this.defaultNew;
		let defaultReply = this.defaultReply;
		const pickedNew = candidates.some((c) => c.suggested_default_new);
		const pickedReply = candidates.some((c) => c.suggested_default_reply);
		for (const candidate of candidates) {
			if (signatures.length >= MAX_MAIL_SIGNATURES) break;
			const text = normalisedText(candidate.html);
			if (known.has(text)) continue;
			known.add(text);
			const name = (candidate.name.trim() || 'Outlook signature').slice(0, 60);
			const id = signatureIdFor(name, taken);
			taken.add(id);
			signatures.push({id, name, html: candidate.html, source: 'outlook', updated_at: now});
			// The suggestion may not be among the chosen ones; then the first import fills in.
			if (defaultNew == null && (candidate.suggested_default_new || !pickedNew)) defaultNew = id;
			if (defaultReply == null && (candidate.suggested_default_reply || !pickedReply)) defaultReply = id;
		}
		return this.save({signatures, default_new: defaultNew, default_reply: defaultReply});
	}

	clearSaveError(): void {
		this.saveError = null;
	}

	private apply(doc: MailSignatureDoc): void {
		this.signatures = doc.signatures;
		this.defaultNew = doc.default_new;
		this.defaultReply = doc.default_reply;
	}

	private async fetch(): Promise<void> {
		this.loading = true;
		this.loadError = false;
		try {
			const response = await http.get<MailSignatureDoc>(Endpoints.KONVO_MAIL_SIGNATURES);
			const doc = coerceDoc(response.body);
			runInAction(() => {
				this.confirmed = doc;
				// Don't clobber an optimistic save that started while we were loading.
				if (!this.saving) this.apply(doc);
				this.loaded = true;
			});
		} catch (error) {
			logger.warn('Failed to load mail signatures', error);
			runInAction(() => {
				this.loadError = true;
				this.loadPromise = null;
			});
		} finally {
			runInAction(() => {
				this.loading = false;
			});
		}
	}
}

export type {MailSignatureCandidate};

export default new MailSignaturesStore();
